import apiService from './api';
import type {
  Product,
  ProductType,
  PimAttribute,
  PimAttributeGroup,
  ApiKey,
  Category,
  ProductRelationship,
  ProductVariantOption,
  ProductFormData,
  ProductTypeFormData,
  PimAttributeFormData,
  PimAttributeGroupFormData,
  ApiKeyFormData,
  CategoryFormData,
  ProductRelationshipFormData,
  ProductVariantOptionFormData,
  VariantMatrixData,
  ProductCategoryAssignment,
  ProductFilters,
  ProductTypeFilters,
  PimAttributeFilters,
  PimAttributeGroupFilters,
  ApiKeyFilters,
  CategoryFilters,
  PimApiResponse,
  PimPaginatedResponse,
  CategoryTreeResponse,
  ProductVariantResponse,
  ProductVariantOptionsResponse,
  ProductRelationshipsResponse,
  PimStatistics,
  BulkProductOperation,
  BulkOperationResult,
  CategoryBulkOperation,
  VariantBulkOperation,
  MediaLibrary,
  PimMedia,
  ProductMedia,
  CategoryMedia,
  ProductTranslation,
  CategoryTranslation,
  AttributeTranslation,
  MediaLibraryFormData,
  PimMediaFormData,
  ProductTranslationFormData,
  CategoryTranslationFormData,
  MediaUploadData,
  MediaFilters,
  MediaLibraryFilters,
  MediaLibraryResponse,
  PimMediaResponse,
  ProductMediaResponse,
  TranslationImportResult,
  ProductWorkflowState,
  ProductWorkflowHistory,
  ProductComment,
  ProductRevision,
  ProductChangeLog,
  WorkflowTransitionData,
  CommentFormData,
  RevisionFormData,
  WorkflowFilters,
  CommentFilters,
  RevisionFilters,
  WorkflowStatistics,
  CommentStatistics,
  RevisionStatistics,
  BulkWorkflowOperation,
  BulkCommentOperation,
  BulkRevisionOperation
} from '../types/pim';

class PimService {
  private baseUrl = '/pim';
  private workflowUrl = '/workflow';
  
  private normalizePagination(pag: any) {
    if (!pag) return { page: 1, limit: 20, total: 0, totalPages: 1 };
    const page = Number(pag.page || pag.current_page || 1);
    const limit = Number(pag.limit || pag.per_page || 20);
    const total = Number(pag.total || 0);
    const totalPages = Number(pag.totalPages || pag.pages || (limit ? Math.max(1, Math.ceil(total / limit)) : 1));
    return { page, limit, total, totalPages };
  }

  // Product operations
  async getProducts(filters: ProductFilters = {}): Promise<PimPaginatedResponse<Product>> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    // Backend expects comma-separated string for status
    if (filters.status?.length) params.append('status', filters.status.join(','));
    if (filters.product_type_id?.length) params.append('productTypeId', filters.product_type_id.join(','));
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    // Backend expects 'sort' and 'order', not 'sort_by' and 'sort_direction'
    if (filters.sort_by) params.append('sort', filters.sort_by);
    if (filters.sort_direction) params.append('order', filters.sort_direction.toUpperCase());
    if (filters.has_media) params.append('has_media', 'true');

    // Handle attribute filters
    if (filters.attribute_filters) {
      Object.entries(filters.attribute_filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(`attr_${key}`, String(value));
        }
      });
    }

    // locale-aware: include current UI language for display_name
    try {
      const { i18n } = await import('i18next');
      const lang = (i18n?.language || '').slice(0,2);
      if (lang) params.append('locale', lang);
    } catch { /* ignore */ }

    const url = `${this.baseUrl}/products?${params}`;
    const response = await apiService.get(url);

    // Transform the response to match expected format
    // Backend returns: { success: true, data: { products: [...], pagination: {...} } }
    // Frontend expects: { data: [...], pagination: { page, limit, total, totalPages } }
    if (response.data.success && response.data.data) {
      const pag = this.normalizePagination(response.data.data.pagination);
      // If backend localized via locale, prefer those fields if present (name/slug/description)
      const items = response.data.data.products;
      return {
        data: items,
        pagination: pag,
      };
    }

    // Fallback if backend already returns a flat response
    if (response.data && response.data.pagination) {
      return {
        data: response.data.data || response.data.products || [],
        pagination: this.normalizePagination(response.data.pagination),
      };
    }

    // Final fallback
    return {
      data: response.data.data || response.data.products || [],
      pagination: this.normalizePagination(response.data.pagination),
    } as any;
  }

  async getProduct(id: number): Promise<PimApiResponse<Product>> {
    const response = await apiService.get(`${this.baseUrl}/products/${id}`);
    return response.data;
  }


  async uploadProductMedia(productId: number, files: File[]): Promise<any> {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const response = await apiService.post(`${this.baseUrl}/products/${productId}/media`, formData);
    return response.data;
  }

  async updateProductMedia(productId: number, mediaId: number, payload: { is_public?: boolean; title?: string; tags?: string[] }): Promise<any> {
    const response = await apiService.patch(`${this.baseUrl}/products/${productId}/media/${mediaId}`, payload);
    return response.data;
  }

  async deleteProductMedia(productId: number, mediaId: number): Promise<any> {
    const response = await apiService.delete(`${this.baseUrl}/products/${productId}/media/${mediaId}`);
    return response.data;
  }

  async getAllowedMediaTags(): Promise<string[]> {
    const response = await apiService.get(`${this.baseUrl}/media/allowed-tags`);
    return response.data?.data?.tags || [];
  }


  async createProduct(data: ProductFormData): Promise<PimApiResponse<Product>> {
    const response = await apiService.post(`${this.baseUrl}/products`, data);
    return response.data;
  }

  async updateProduct(id: number, data: Partial<ProductFormData>): Promise<PimApiResponse<Product>> {
    const response = await apiService.put(`${this.baseUrl}/products/${id}`, data);
    return response.data;
  }

  async deleteProduct(id: number): Promise<PimApiResponse<void>> {
    const response = await apiService.delete(`${this.baseUrl}/products/${id}`);
    return response.data;
  }

  async bulkProductOperation(operation: BulkProductOperation): Promise<PimApiResponse<BulkOperationResult>> {
    const response = await apiService.post(`${this.baseUrl}/products/bulk`, operation);
    return response.data;
  }

  // Product Type operations
  async getProductTypes(filters: ProductTypeFilters = {}): Promise<PimPaginatedResponse<ProductType>> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_direction) params.append('sort_direction', filters.sort_direction);

    const response = await apiService.get(`${this.baseUrl}/product-types?${params}`);
    
    if (response.data.success && response.data.data) {
      return {
        data: response.data.data.productTypes || response.data.data.product_types,
        pagination: this.normalizePagination(response.data.data.pagination)
      };
    }
    if (response.data && response.data.pagination) {
      return {
        data: response.data.data || response.data.productTypes || response.data.product_types || [],
        pagination: this.normalizePagination(response.data.pagination)
      } as any;
    }
    return response.data;
  }

  async getProductType(id: number): Promise<PimApiResponse<ProductType>> {
    const response = await apiService.get(`${this.baseUrl}/product-types/${id}`);
    return response.data;
  }

  async createProductType(data: ProductTypeFormData): Promise<PimApiResponse<ProductType>> {
    const response = await apiService.post(`${this.baseUrl}/product-types`, data);
    return response.data;
  }

  async updateProductType(id: number, data: Partial<ProductTypeFormData>): Promise<PimApiResponse<ProductType>> {
    const response = await apiService.put(`${this.baseUrl}/product-types/${id}`, data);
    return response.data;
  }

  async deleteProductType(id: number): Promise<PimApiResponse<void>> {
    const response = await apiService.delete(`${this.baseUrl}/product-types/${id}`);
    return response.data;
  }

  // Attribute Group operations
  async getAttributeGroups(filters: PimAttributeGroupFilters = {}): Promise<PimPaginatedResponse<PimAttributeGroup>> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.is_global !== undefined) params.append('is_global', filters.is_global.toString());
    if (filters.product_type_id) params.append('product_type_id', filters.product_type_id.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_direction) params.append('sort_direction', filters.sort_direction);

    const response = await apiService.get(`${this.baseUrl}/attribute-groups?${params}`);
    
    if (response.data.success && response.data.data) {
      return {
        data: response.data.data.attributeGroups || response.data.data.groups,
        pagination: this.normalizePagination(response.data.data.pagination)
      };
    }
    if (response.data && response.data.pagination) {
      return {
        data: response.data.data || response.data.attributeGroups || response.data.groups || [],
        pagination: this.normalizePagination(response.data.pagination)
      } as any;
    }
    return response.data;
  }

  async getAttributeGroup(id: number): Promise<PimApiResponse<PimAttributeGroup>> {
    const response = await apiService.get(`${this.baseUrl}/attribute-groups/${id}`);
    return response.data;
  }

  async createAttributeGroup(data: PimAttributeGroupFormData): Promise<PimApiResponse<PimAttributeGroup>> {
    // Map frontend field names to backend expected names (backend uses snake_case)
    const backendData: any = {
      name: data.name,
      code: data.code,
      description: data.description,
      sortOrder: data.sortOrder
    };

    // LOGIC FIX: If productTypeId is set, DON'T send isGlobal (backend will auto-set it)
    if (data.productTypeId !== undefined && data.productTypeId !== null) {
      backendData.productTypeId = data.productTypeId;
      // Do NOT send isGlobal - backend will set it to false automatically
    } else {
      // Only send isGlobal if productTypeId is not set
      backendData.isGlobal = data.isGlobal !== undefined ? data.isGlobal : true;
    }

    const response = await apiService.post(`${this.baseUrl}/attribute-groups`, backendData);
    return response.data;
  }

  async updateAttributeGroup(id: number, data: Partial<PimAttributeGroupFormData>): Promise<PimApiResponse<PimAttributeGroup>> {
    // Map frontend field names to backend expected names
    const backendData: any = {};
    if (data.name !== undefined) backendData.name = data.name;
    if (data.code !== undefined) backendData.code = data.code;
    if (data.description !== undefined) backendData.description = data.description;
    if (data.sortOrder !== undefined) backendData.sortOrder = data.sortOrder;

    // LOGIC FIX: If productTypeId is set, DON'T send isGlobal (backend will auto-set it)
    if (data.productTypeId !== undefined) {
      backendData.productTypeId = data.productTypeId;
      // Do NOT send isGlobal - backend will handle it automatically
    } else if (data.isGlobal !== undefined) {
      // Only send isGlobal if productTypeId is not being changed
      backendData.isGlobal = data.isGlobal;
    }

    const response = await apiService.put(`${this.baseUrl}/attribute-groups/${id}`, backendData);
    return response.data;
  }

  async deleteAttributeGroup(id: number): Promise<PimApiResponse<void>> {
    const response = await apiService.delete(`${this.baseUrl}/attribute-groups/${id}`);
    return response.data;
  }

  // Attribute operations
  async getAttributes(filters: PimAttributeFilters = {}): Promise<PimPaginatedResponse<PimAttribute>> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.group_id?.length) params.append('group_id', filters.group_id.join(','));
    if (filters.type?.length) params.append('type', filters.type.join(','));
    if (filters.is_required !== undefined) params.append('is_required', filters.is_required.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_direction) params.append('sort_direction', filters.sort_direction);

    const response = await apiService.get(`${this.baseUrl}/attributes?${params}`);
    
    if (response.data.success && response.data.data) {
      return {
        data: response.data.data.attributes,
        pagination: this.normalizePagination(response.data.data.pagination)
      };
    }
    if (response.data && response.data.pagination) {
      return {
        data: response.data.data || response.data.attributes || [],
        pagination: this.normalizePagination(response.data.pagination)
      } as any;
    }
    return response.data;
  }

  async getAttribute(id: number): Promise<PimApiResponse<PimAttribute>> {
    const response = await apiService.get(`${this.baseUrl}/attributes/${id}`);
    return response.data;
  }

  async createAttribute(data: PimAttributeFormData): Promise<PimApiResponse<PimAttribute>> {
    // Map frontend field names to backend expected names
    const backendData = {
      code: data.code,
      label: data.name, // frontend: name -> backend: label
      description: data.description || '',
      type: data.type,
      groupId: data.group_id, // frontend: group_id -> backend: groupId
      sortOrder: data.position, // frontend: position -> backend: sortOrder
      isRequired: data.is_required, // frontend: is_required -> backend: isRequired (saved as 'required' in DB)
      isUnique: data.is_unique,
      isFilterable: data.is_filterable,
      isSearchable: data.is_searchable,
      isActive: data.is_active, // frontend: is_active -> backend: isActive
      isPublic: data.is_public || false,
      validationRules: data.validation_json || {},
      options: data.options_json?.options || []
    };

    console.log('🔧 Frontend form data:', data);
    console.log('🔧 Backend payload:', backendData);

    const response = await apiService.post(`${this.baseUrl}/attributes`, backendData);
    return response.data;
  }

  async updateAttribute(id: number, data: Partial<PimAttributeFormData>): Promise<PimApiResponse<PimAttribute>> {
    // Map frontend field names to backend expected names
    const backendData: any = {};
    if (data.code !== undefined) backendData.code = data.code;
    if (data.name !== undefined) backendData.label = data.name;
    if (data.description !== undefined) backendData.description = data.description;
    if (data.type !== undefined) backendData.type = data.type;
    if (data.group_id !== undefined) backendData.groupId = data.group_id;
    if (data.position !== undefined) backendData.sortOrder = data.position;
    if (data.is_required !== undefined) backendData.isRequired = data.is_required;
    if (data.is_unique !== undefined) backendData.isUnique = data.is_unique;
    if (data.is_filterable !== undefined) backendData.isFilterable = data.is_filterable;
    if (data.is_searchable !== undefined) backendData.isSearchable = data.is_searchable;
    if (data.is_active !== undefined) backendData.isActive = data.is_active;
    if (data.is_public !== undefined) backendData.isPublic = data.is_public;
    if (data.validation_json !== undefined) backendData.validationRules = data.validation_json;
    if (data.options_json !== undefined) backendData.options = data.options_json?.options || [];

    console.log('🔧 UPDATE Frontend form data:', data);
    console.log('🔧 UPDATE Backend payload:', backendData);
    console.log('🔧 UPDATE URL:', `${this.baseUrl}/attributes/${id}`);

    const response = await apiService.put(`${this.baseUrl}/attributes/${id}`, backendData);
    return response.data;
  }

  async deleteAttribute(id: number): Promise<PimApiResponse<void>> {
    const response = await apiService.delete(`${this.baseUrl}/attributes/${id}`);
    return response.data;
  }

  // API Key operations
  async getApiKeys(filters: ApiKeyFilters = {}): Promise<PimPaginatedResponse<ApiKey>> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_direction) params.append('sort_direction', filters.sort_direction);

    const response = await apiService.get(`${this.baseUrl}/api-keys?${params}`);
    
    if (response.data.success && response.data.data) {
      return {
        data: response.data.data.apiKeys,
        pagination: this.normalizePagination(response.data.data.pagination)
      };
    }
    if (response.data && response.data.pagination) {
      return {
        data: response.data.data || response.data.apiKeys || [],
        pagination: this.normalizePagination(response.data.pagination)
      } as any;
    }
    return response.data;
  }

  async getApiKey(id: number): Promise<PimApiResponse<ApiKey>> {
    const response = await apiService.get(`${this.baseUrl}/api-keys/${id}`);
    return response.data;
  }

  async createApiKey(data: ApiKeyFormData): Promise<PimApiResponse<ApiKey>> {
    const response = await apiService.post(`${this.baseUrl}/api-keys`, data);
    return response.data;
  }

  async updateApiKey(id: number, data: Partial<ApiKeyFormData>): Promise<PimApiResponse<ApiKey>> {
    const response = await apiService.put(`${this.baseUrl}/api-keys/${id}`, data);
    return response.data;
  }

  async deleteApiKey(id: number): Promise<PimApiResponse<void>> {
    const response = await apiService.delete(`${this.baseUrl}/api-keys/${id}`);
    return response.data;
  }

  async regenerateApiKey(id: number): Promise<PimApiResponse<ApiKey>> {
    const response = await apiService.post(`${this.baseUrl}/api-keys/${id}/regenerate`);
    return response.data;
  }

  async getApiKeyUsageStatistics(id: number): Promise<PimApiResponse<any>> {
    const response = await apiService.get(`${this.baseUrl}/api-keys/${id}/usage`);
    return response.data;
  }

  async testApiKey(id: number, endpoint: string): Promise<PimApiResponse<any>> {
    const response = await apiService.post(`${this.baseUrl}/api-keys/${id}/test`, { endpoint });
    return response.data;
  }

  // Statistics
  async getStatistics(): Promise<PimApiResponse<PimStatistics>> {
    const response = await apiService.get(`${this.baseUrl}/statistics`);
    return response.data;
  }

  // Helper methods for dropdown lists
  async getProductTypesForDropdown(): Promise<PimApiResponse<Array<{id: number, name: string}>>> {
    const response = await apiService.get(`${this.baseUrl}/product-types/dropdown`);
    return response.data;
  }

  async getAttributeGroupsForDropdown(): Promise<PimApiResponse<Array<{id: number, name: string}>>> {
    const response = await apiService.get(`${this.baseUrl}/attribute-groups/dropdown`);
    return response.data;
  }

  async getAttributesForDropdown(groupId?: number): Promise<PimApiResponse<Array<{id: number, name: string, code: string, type: string}>>> {
    const params = groupId ? `?group_id=${groupId}` : '';
    const response = await apiService.get(`${this.baseUrl}/attributes/dropdown${params}`);
    return response.data;
  }

  // Validation methods
  async validateSku(sku: string, excludeId?: number): Promise<PimApiResponse<{available: boolean}>> {
    const params = new URLSearchParams();
    params.append('sku', sku);
    if (excludeId) params.append('exclude_id', excludeId.toString());
    
    const response = await apiService.get(`${this.baseUrl}/products/validate-sku?${params}`);
    return response.data;
  }

  async validateSlug(slug: string, excludeId?: number): Promise<PimApiResponse<{available: boolean}>> {
    const params = new URLSearchParams();
    params.append('slug', slug);
    if (excludeId) params.append('exclude_id', excludeId.toString());
    
    const response = await apiService.get(`${this.baseUrl}/products/validate-slug?${params}`);
    return response.data;
  }

  async validateAttributeCode(code: string, excludeId?: number): Promise<PimApiResponse<{available: boolean}>> {
    const params = new URLSearchParams();
    params.append('code', code);
    if (excludeId) params.append('exclude_id', excludeId.toString());
    
    const response = await apiService.get(`${this.baseUrl}/attributes/validate-code?${params}`);
    return response.data;
  }

  async validateAttributeGroupCode(code: string, excludeId?: number): Promise<PimApiResponse<{available: boolean}>> {
    const params = new URLSearchParams();
    params.append('code', code);
    if (excludeId) params.append('exclude_id', excludeId.toString());
    
    const response = await apiService.get(`${this.baseUrl}/attribute-groups/validate-code?${params}`);
    return response.data;
  }

  async getAttributeUsage(attributeId: number): Promise<PimApiResponse<{
    attribute: { id: number; code: string; label: string; type: string };
    statistics: {
      productsUsing: number;
      withValues: number;
      emptyValues: number;
      fillRate: number;
      lastUsed: string;
      lastUsedDate: string | null;
      mostRecentProduct: { id: number; name: string; sku: string } | null;
      averageValueLength: number | null;
    };
  }>> {
    const response = await apiService.get(`${this.baseUrl}/attributes/${attributeId}/usage`);
    return response.data;
  }

  async validateProductTypeCode(code: string, excludeId?: number): Promise<PimApiResponse<{available: boolean}>> {
    const params = new URLSearchParams();
    params.append('code', code);
    if (excludeId) params.append('exclude_id', excludeId.toString());
    
    const response = await apiService.get(`${this.baseUrl}/product-types/validate-code?${params}`);
    return response.data;
  }

  // Category operations
  async getCategories(filters: CategoryFilters = {}): Promise<PimPaginatedResponse<Category>> {
    const params = new URLSearchParams();
    
    if (filters.parentId !== undefined) params.append('parentId', filters.parentId.toString());
    if (filters.includeProducts) params.append('includeProducts', 'true');
    if (filters.includeInactive) params.append('includeInactive', 'true');
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_direction) params.append('sort_direction', filters.sort_direction);

    const response = await apiService.get(`${this.baseUrl}/categories?${params}`);
    
    return {
      data: response.data.data || [],
      pagination: this.normalizePagination(response.data.pagination),
    };
  }

  async getCategory(id: number, includeProducts: boolean = false): Promise<PimApiResponse<Category>> {
    const params = new URLSearchParams();
    if (includeProducts) params.append('includeProducts', 'true');
    
    const response = await apiService.get(`${this.baseUrl}/categories/${id}?${params}`);
    return response.data;
  }

  async getCategoryTree(includeProducts: boolean = false): Promise<CategoryTreeResponse> {
    const params = new URLSearchParams();
    if (includeProducts) params.append('includeProducts', 'true');
    
    const url = `${this.baseUrl}/categories/tree${params.toString() ? `?${params}` : ''}`;
    const response = await apiService.get(url);
    return response.data;
  }

  async createCategory(data: CategoryFormData): Promise<PimApiResponse<Category>> {
    // Map frontend field names to backend expected names  
    const backendData: any = {
      name: data.name,
      slug: data.slug
    };
    
    // Only include optional fields if they have values
    if (data.description !== undefined) backendData.description = data.description;
    if (data.parentId !== undefined) backendData.parentId = data.parentId;
    if (data.sortOrder !== undefined) backendData.sortOrder = data.sortOrder;
    if (data.isActive !== undefined) backendData.isActive = data.isActive;
    if (data.imageUrl !== undefined) backendData.imageUrl = data.imageUrl;
    if (data.metaTitle !== undefined) backendData.metaTitle = data.metaTitle;
    if (data.metaDescription !== undefined) backendData.metaDescription = data.metaDescription;
    
    const response = await apiService.post(`${this.baseUrl}/categories`, backendData);
    return response.data;
  }

  async updateCategory(id: number, data: Partial<CategoryFormData>): Promise<PimApiResponse<Category>> {
    // Map frontend field names to backend expected names
    const backendData: any = {};
    if (data.name !== undefined) backendData.name = data.name;
    if (data.slug !== undefined) backendData.slug = data.slug;
    if (data.description !== undefined) backendData.description = data.description;
    if (data.parentId !== undefined) backendData.parentId = data.parentId;
    if (data.sortOrder !== undefined) backendData.sortOrder = data.sortOrder;
    if (data.isActive !== undefined) backendData.isActive = data.isActive;
    if (data.imageUrl !== undefined) backendData.imageUrl = data.imageUrl;
    if (data.metaTitle !== undefined) backendData.metaTitle = data.metaTitle;
    if (data.metaDescription !== undefined) backendData.metaDescription = data.metaDescription;
    
    const response = await apiService.put(`${this.baseUrl}/categories/${id}`, backendData);
    return response.data;
  }

  async deleteCategory(id: number): Promise<PimApiResponse<void>> {
    const response = await apiService.delete(`${this.baseUrl}/categories/${id}`);
    return response.data;
  }

  async assignProductCategories(productId: number, data: ProductCategoryAssignment): Promise<PimApiResponse<void>> {
    const response = await apiService.put(`${this.baseUrl}/products/${productId}/categories`, data);
    return response.data;
  }

  async categoryBulkOperation(operation: CategoryBulkOperation): Promise<PimApiResponse<BulkOperationResult>> {
    const response = await apiService.post(`${this.baseUrl}/categories/bulk`, operation);
    return response.data;
  }

  async productBulkOperation(operation: ProductBulkOperation): Promise<PimApiResponse<BulkOperationResult>> {
    const response = await apiService.post(`${this.baseUrl}/products/bulk`, operation);
    return response.data;
  }

  // Product Variants operations
  async getProductVariants(productId: number): Promise<ProductVariantResponse> {
    const response = await apiService.get(`${this.baseUrl}/products/${productId}/variants`);
    return response.data;
  }

  async createProductVariant(productId: number, data: ProductRelationshipFormData): Promise<PimApiResponse<ProductRelationship>> {
    const response = await apiService.post(`${this.baseUrl}/products/${productId}/variants`, data);
    return response.data;
  }

  async getProductVariantOptions(productId: number): Promise<ProductVariantOptionsResponse> {
    const response = await apiService.get(`${this.baseUrl}/products/${productId}/variant-options`);
    return response.data;
  }

  async createProductVariantOption(productId: number, data: ProductVariantOptionFormData): Promise<PimApiResponse<ProductVariantOption>> {
    const response = await apiService.post(`${this.baseUrl}/products/${productId}/variant-options`, data);
    return response.data;
  }

  async updateProductVariantOption(productId: number, optionId: number, data: Partial<ProductVariantOptionFormData>): Promise<PimApiResponse<ProductVariantOption>> {
    const response = await apiService.put(`${this.baseUrl}/products/${productId}/variant-options/${optionId}`, data);
    return response.data;
  }

  async deleteProductVariantOption(productId: number, optionId: number): Promise<PimApiResponse<void>> {
    const response = await apiService.delete(`${this.baseUrl}/products/${productId}/variant-options/${optionId}`);
    return response.data;
  }

  async createVariantMatrix(productId: number, data: VariantMatrixData): Promise<PimApiResponse<ProductVariantOption[]>> {
    const response = await apiService.post(`${this.baseUrl}/products/${productId}/variant-options/bulk`, data);
    return response.data;
  }

  // Product Relationships operations
  async getProductRelationships(productId: number, relationshipType?: string): Promise<ProductRelationshipsResponse> {
    const params = new URLSearchParams();
    if (relationshipType) params.append('relationshipType', relationshipType);
    
    const response = await apiService.get(`${this.baseUrl}/products/${productId}/relationships?${params}`);
    return response.data;
  }

  async createProductRelationship(productId: number, data: ProductRelationshipFormData): Promise<PimApiResponse<ProductRelationship>> {
    const response = await apiService.post(`${this.baseUrl}/products/${productId}/relationships`, data);
    return response.data;
  }

  async updateProductRelationship(productId: number, relationshipId: number, data: Partial<ProductRelationshipFormData>): Promise<PimApiResponse<ProductRelationship>> {
    const response = await apiService.put(`${this.baseUrl}/products/${productId}/relationships/${relationshipId}`, data);
    return response.data;
  }

  async deleteProductRelationship(productId: number, relationshipId: number): Promise<PimApiResponse<void>> {
    const response = await apiService.delete(`${this.baseUrl}/products/${productId}/relationships/${relationshipId}`);
    return response.data;
  }

  async variantBulkOperation(operation: VariantBulkOperation): Promise<PimApiResponse<BulkOperationResult>> {
    const response = await apiService.post(`${this.baseUrl}/variants/bulk`, operation);
    return response.data;
  }

  // Media Library methods
  async getMediaLibraries(filters: MediaLibraryFilters = {}): Promise<MediaLibraryResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await apiService.get(`${this.baseUrl}/media/libraries?${params}`);
    return response.data;
  }

  async createMediaLibrary(data: MediaLibraryFormData): Promise<PimApiResponse<MediaLibrary>> {
    const response = await apiService.post(`${this.baseUrl}/media/libraries`, data);
    return response.data;
  }

  async updateMediaLibrary(id: number, data: Partial<MediaLibraryFormData>): Promise<PimApiResponse<MediaLibrary>> {
    const response = await apiService.put(`${this.baseUrl}/media/libraries/${id}`, data);
    return response.data;
  }

  async deleteMediaLibrary(id: number, force: boolean = false): Promise<PimApiResponse<void>> {
    const url = force
      ? `${this.baseUrl}/media/libraries/${id}?force=true`
      : `${this.baseUrl}/media/libraries/${id}`;
    const response = await apiService.delete(url);
    return response.data;
  }

  // Media methods
  async getMedia(filters: MediaFilters = {}): Promise<PimMediaResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await apiService.get(`${this.baseUrl}/media?${params}`);
    return response.data;
  }

  async uploadMedia(data: MediaUploadData): Promise<PimApiResponse<PimMedia>> {
    const formData = new FormData();
    formData.append('file', data.file);
    
    if (data.library_id) formData.append('library_id', data.library_id.toString());
    if (data.product_id) formData.append('product_id', data.product_id.toString());
    if (data.title) formData.append('title', data.title);
    if (data.alt_text) formData.append('alt_text', data.alt_text);
    if (data.description) formData.append('description', data.description);
    if (data.tags) formData.append('tags', JSON.stringify(data.tags));
    if (data.is_public !== undefined) formData.append('is_public', data.is_public.toString());

    const response = await apiService.post(`${this.baseUrl}/media/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateMedia(id: number, data: Partial<PimMediaFormData>): Promise<PimApiResponse<PimMedia>> {
    const response = await apiService.put(`${this.baseUrl}/media/${id}`, data);
    return response.data;
  }

  async deleteMedia(id: number): Promise<PimApiResponse<void>> {
    const response = await apiService.delete(`${this.baseUrl}/media/${id}`);
    return response.data;
  }

  // Product Media methods
  async getProductMedia(productId: number, type?: string): Promise<ProductMediaResponse> {
    const params = type ? `?type=${type}` : '';
    const response = await apiService.get(`${this.baseUrl}/products/${productId}/media${params}`);
    return response.data;
  }

  async assignMediaToProduct(productId: number, data: { media_id: number; type?: string; position?: number }): Promise<PimApiResponse<ProductMedia>> {
    const response = await apiService.post(`${this.baseUrl}/media/products/${productId}/assign`, data);
    return response.data;
  }

  async removeMediaFromProduct(productId: number, mediaId: number): Promise<PimApiResponse<void>> {
    const response = await apiService.delete(`${this.baseUrl}/media/products/${productId}/media/${mediaId}`);
    return response.data;
  }

  // Translation methods
  async getProductTranslations(productId: number, locale?: string): Promise<PimApiResponse<ProductTranslation[]>> {
    const params = locale ? `?locale=${locale}` : '';
    const response = await apiService.get(`${this.baseUrl}/products/${productId}/translations${params}`);
    return response.data;
  }

  async upsertProductTranslation(productId: number, locale: string, data: ProductTranslationFormData): Promise<PimApiResponse<ProductTranslation>> {
    const response = await apiService.put(`${this.baseUrl}/products/${productId}/translations/${locale}`, data);
    return response.data;
  }

  async deleteProductTranslation(productId: number, locale: string): Promise<PimApiResponse<void>> {
    const response = await apiService.delete(`${this.baseUrl}/products/${productId}/translations/${locale}`);
    return response.data;
  }

  async exportProductTranslations(productId: number, format: 'json' | 'csv' = 'json'): Promise<Blob> {
    const response = await apiService.get(`${this.baseUrl}/products/${productId}/translations/export?format=${format}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async importProductTranslations(productId: number, file: File): Promise<PimApiResponse<TranslationImportResult>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiService.post(`${this.baseUrl}/products/${productId}/translations/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async validateTranslationSlug(locale: string, slug: string, productId?: number): Promise<PimApiResponse<{ available: boolean; slug: string; locale: string }>> {
    const params = new URLSearchParams({ locale, slug });
    if (productId) params.append('productId', productId.toString());
    
    const response = await apiService.get(`${this.baseUrl}/translations/validate-slug?${params}`);
    return response.data;
  }

  async getCategoryTranslations(categoryId: number, locale?: string): Promise<PimApiResponse<CategoryTranslation[]>> {
    const params = locale ? `?locale=${locale}` : '';
    const response = await apiService.get(`${this.baseUrl}/translations/categories/${categoryId}${params}`);
    return response.data;
  }

  async upsertCategoryTranslation(categoryId: number, locale: string, data: CategoryTranslationFormData): Promise<PimApiResponse<CategoryTranslation>> {
    const response = await apiService.put(`${this.baseUrl}/translations/categories/${categoryId}/${locale}`, data);
    return response.data;
  }

  async getAttributeTranslations(attributeId: number, locale?: string): Promise<PimApiResponse<AttributeTranslation[]>> {
    const params = locale ? `?locale=${locale}` : '';
    const response = await apiService.get(`${this.baseUrl}/translations/attributes/${attributeId}${params}`);
    return response.data;
  }

  // Workflow methods
  async getProductWorkflowState(productId: number): Promise<PimApiResponse<ProductWorkflowState>> {
    const response = await apiService.get(`${this.workflowUrl}/products/${productId}/workflow`);
    return response.data;
  }

  async transitionWorkflowState(productId: number, data: WorkflowTransitionData): Promise<PimApiResponse<ProductWorkflowState>> {
    const response = await apiService.post(`${this.workflowUrl}/products/${productId}/workflow/transition`, data);
    return response.data;
  }

  async getWorkflowHistory(productId: number, options: { limit?: number; offset?: number } = {}): Promise<PimApiResponse<ProductWorkflowHistory[]>> {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    
    const response = await apiService.get(`${this.workflowUrl}/products/${productId}/workflow/history?${params}`);
    return response.data;
  }

  async getProductsByWorkflowState(filters: WorkflowFilters = {}): Promise<PimApiResponse<ProductWorkflowState[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await apiService.get(`${this.workflowUrl}/dashboard/products?${params}`);
    return response.data;
  }

  async getWorkflowStatistics(options: { startDate?: string; endDate?: string; userId?: number } = {}): Promise<PimApiResponse<WorkflowStatistics>> {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await apiService.get(`${this.workflowUrl}/dashboard/statistics?${params}`);
    return response.data;
  }

  async bulkWorkflowOperation(operation: BulkWorkflowOperation): Promise<PimApiResponse<any>> {
    const response = await apiService.post(`${this.workflowUrl}/bulk/workflow`, operation);
    return response.data;
  }

  // Comment methods
  async getProductComments(productId: number, filters: CommentFilters = {}): Promise<PimApiResponse<ProductComment[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await apiService.get(`${this.workflowUrl}/products/${productId}/comments?${params}`);
    return response.data;
  }

  async createComment(productId: number, data: CommentFormData): Promise<PimApiResponse<ProductComment>> {
    const response = await apiService.post(`${this.workflowUrl}/products/${productId}/comments`, data);
    return response.data;
  }

  async updateComment(commentId: number, data: Partial<CommentFormData>): Promise<PimApiResponse<ProductComment>> {
    const response = await apiService.put(`${this.workflowUrl}/comments/${commentId}`, data);
    return response.data;
  }

  async deleteComment(commentId: number): Promise<PimApiResponse<void>> {
    const response = await apiService.delete(`${this.workflowUrl}/comments/${commentId}`);
    return response.data;
  }

  async toggleCommentResolution(commentId: number, resolved: boolean): Promise<PimApiResponse<ProductComment>> {
    const response = await apiService.post(`${this.workflowUrl}/comments/${commentId}/resolve`, { resolved });
    return response.data;
  }

  async getCommentStatistics(productId: number): Promise<PimApiResponse<CommentStatistics>> {
    const response = await apiService.get(`${this.workflowUrl}/products/${productId}/comments/statistics`);
    return response.data;
  }

  async getUserComments(userId: number, options: { limit?: number; offset?: number; productId?: number } = {}): Promise<PimApiResponse<ProductComment[]>> {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await apiService.get(`${this.workflowUrl}/users/${userId}/comments?${params}`);
    return response.data;
  }

  async bulkCommentOperation(operation: BulkCommentOperation): Promise<PimApiResponse<any>> {
    const response = await apiService.post(`${this.workflowUrl}/bulk/comments`, operation);
    return response.data;
  }

  // Revision methods
  async getProductRevisions(productId: number, filters: RevisionFilters = {}): Promise<PimApiResponse<ProductRevision[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await apiService.get(`${this.workflowUrl}/products/${productId}/revisions?${params}`);
    return response.data;
  }

  async createRevision(productId: number, data: RevisionFormData): Promise<PimApiResponse<ProductRevision>> {
    const response = await apiService.post(`${this.workflowUrl}/products/${productId}/revisions`, data);
    return response.data;
  }

  async getRevision(revisionId: number): Promise<PimApiResponse<ProductRevision>> {
    const response = await apiService.get(`${this.workflowUrl}/revisions/${revisionId}`);
    return response.data;
  }

  async updateRevision(revisionId: number, data: Partial<RevisionFormData>): Promise<PimApiResponse<ProductRevision>> {
    const response = await apiService.put(`${this.workflowUrl}/revisions/${revisionId}`, data);
    return response.data;
  }

  async deleteRevision(revisionId: number): Promise<PimApiResponse<void>> {
    const response = await apiService.delete(`${this.workflowUrl}/revisions/${revisionId}`);
    return response.data;
  }

  async restoreRevision(revisionId: number): Promise<PimApiResponse<ProductRevision>> {
    const response = await apiService.post(`${this.workflowUrl}/revisions/${revisionId}/restore`);
    return response.data;
  }

  async compareRevisions(revisionId1: number, revisionId2: number): Promise<PimApiResponse<any>> {
    const response = await apiService.get(`${this.workflowUrl}/revisions/${revisionId1}/compare/${revisionId2}`);
    return response.data;
  }

  async getCurrentRevision(productId: number): Promise<PimApiResponse<ProductRevision>> {
    const response = await apiService.get(`${this.workflowUrl}/products/${productId}/revisions/current`);
    return response.data;
  }

  async getRevisionStatistics(productId: number): Promise<PimApiResponse<RevisionStatistics>> {
    const response = await apiService.get(`${this.workflowUrl}/products/${productId}/revisions/statistics`);
    return response.data;
  }

  async bulkRevisionOperation(operation: BulkRevisionOperation): Promise<PimApiResponse<any>> {
    const response = await apiService.post(`${this.workflowUrl}/bulk/revisions`, operation);
    return response.data;
  }

  // Settings methods
  async getSettings(): Promise<any> {
    const response = await apiService.get(`${this.baseUrl}/settings`);
    // Backend returns: { success: true, message: '...', data: { general: {}, products: {}, ... } }
    // Extract the actual settings object from response.data.data
    return response.data.data || response.data;
  }

  async updateSettings(data: any): Promise<PimApiResponse<any>> {
    const response = await apiService.put(`${this.baseUrl}/settings`, data);
    return response.data;
  }

  async testDatabaseConnection(): Promise<PimApiResponse<any>> {
    const response = await apiService.get(`${this.baseUrl}/settings/test/database`);
    return response.data;
  }

  async testEmailConfiguration(): Promise<PimApiResponse<any>> {
    const response = await apiService.get(`${this.baseUrl}/settings/test/email`);
    return response.data;
  }

  async exportSettings(): Promise<Blob> {
    const response = await apiService.get(`${this.baseUrl}/settings/export`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async importSettings(file: File): Promise<PimApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiService.post(`${this.baseUrl}/settings/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Workflow Dashboard methods
  async getWorkflowDashboardStatistics(): Promise<PimApiResponse<any>> {
    const response = await apiService.get(`${this.baseUrl}/workflow/dashboard/statistics`);
    return response.data;
  }

  async getWorkflowProducts(params: any): Promise<PimApiResponse<any>> {
    const response = await apiService.get(`${this.baseUrl}/workflow/dashboard/products`, { params });
    return response.data;
  }

  async getWorkflowReviewers(): Promise<PimApiResponse<any>> {
    const response = await apiService.get(`${this.baseUrl}/workflow/dashboard/reviewers`);
    return response.data;
  }
}

export const pimService = new PimService();
export default pimService;
